/**
 * DV-TEMP-002: QC Summary Card Component
 * Display QC summary with persona, scores, and pass/fail status
 */

import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  User,
  BarChart3,
  Clock,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useQcSummary } from '@/hooks/useQcSummary';
import type { StageName } from '@/lib/shared-types';
import { getDemoError, logDemoError } from '@/lib/demo-error-handler';
import { PermissionNotice, NotFoundNotice, ServerErrorNotice } from './ErrorNotices';
import type { LogEntry } from './LogsPanel';

export interface QCSummaryCardProps {
  executionId: string;
  stage: StageName;
  onLog?: (entry: LogEntry) => void;
  className?: string;
  'data-testid'?: string;
}

// Format timestamp for display
const formatTimestamp = (timestamp: string): string => {
  try {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return timestamp;
  }
};

// Get decision icon and color
const getDecisionDisplay = (decision: string) => {
  switch (decision) {
    case 'pass':
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        color: 'var(--success)',
        label: 'Pass'
      };
    case 'borderline':
      return {
        icon: <AlertTriangle className="w-4 h-4" />,
        color: 'var(--warning)',
        label: 'Borderline'
      };
    case 'fail':
      return {
        icon: <XCircle className="w-4 h-4" />,
        color: 'var(--danger)',
        label: 'Fail'
      };
    default:
      return {
        icon: <AlertCircle className="w-4 h-4" />,
        color: 'var(--fg-muted)',
        label: 'Unknown'
      };
  }
};

// Format score as percentage
const formatScore = (score: number): string => {
  return `${Math.round(score * 100)}%`;
};

export function QCSummaryCard({
  executionId,
  stage,
  onLog,
  className,
  'data-testid': testId = 'qc-summary-card'
}: QCSummaryCardProps) {
  const { data: qcSummary, isLoading, isError, error, refetch } = useQcSummary(executionId, stage);

  // Extract demo error and log structured errors
  const demoError = isError && error ? getDemoError(error) : null;
  if (demoError && onLog) {
    // Log structured error only once per error instance
    const errorKey = `qc-${demoError.correlationId || demoError.status}-${executionId}-${stage}`;
    if (!sessionStorage.getItem(errorKey)) {
      onLog(logDemoError(demoError));
      sessionStorage.setItem(errorKey, 'logged');
    }
  }

  // Loading state
  if (isLoading) {
    return (
      <div
        className={cn("border rounded-lg p-4", className)}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--border-default)'
        }}
        data-testid={`${testId}-loading`}
      >
        <div className="flex items-center justify-center gap-2 text-sm" style={{ color: 'var(--fg-muted)' }}>
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading QC summary...
        </div>
      </div>
    );
  }

  // Error state with DV-TEMP-003 enhanced error handling
  if (isError && demoError) {
    if (demoError.type === 'permission_denied') {
      return (
        <PermissionNotice
          error={demoError}
          onRetry={demoError.retryable ? refetch : undefined}
          className={className}
          data-testid={`${testId}-error`}
        />
      );
    }

    if (demoError.type === 'not_found') {
      // Special handling for 404 - could be "no QC data yet" rather than error
      const is404 = error?.status === 404;
      if (is404) {
        return (
          <div
            className={cn("border rounded-lg p-4", className)}
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)'
            }}
            data-testid={`${testId}-error`}
          >
            <div className="text-center">
              <AlertCircle
                className="w-6 h-6 mx-auto mb-2"
                style={{ color: 'var(--fg-muted)' }}
              />
              <div className="text-sm" style={{ color: 'var(--fg-muted)' }}>
                No QC summary yet
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--fg-muted)' }}>
                QC evaluation has not been completed for this stage
              </div>
            </div>
          </div>
        );
      }

      return (
        <NotFoundNotice
          error={demoError}
          executionId={executionId}
          stage={stage}
          onRetry={demoError.retryable ? refetch : undefined}
          className={className}
          data-testid={`${testId}-error`}
        />
      );
    }

    if (demoError.type === 'server_error') {
      return (
        <ServerErrorNotice
          error={demoError}
          onRetry={refetch}
          className={className}
          data-testid={`${testId}-error`}
        />
      );
    }

    // Fallback error display
    return (
      <div
        className={cn("border rounded-lg p-4", className)}
        style={{
          backgroundColor: 'var(--bg-surface)',
          borderColor: 'var(--danger)'
        }}
        data-testid={`${testId}-error`}
      >
        <div className="text-center">
          <AlertCircle
            className="w-6 h-6 mx-auto mb-2"
            style={{ color: 'var(--danger)' }}
          />
          <div className="text-sm" style={{ color: 'var(--danger)' }}>
            {demoError.userMessage}
          </div>
        </div>
      </div>
    );
  }

  if (!qcSummary) return null;

  const decisionDisplay = getDecisionDisplay(qcSummary.decision);

  return (
    <div
      className={cn("border rounded-lg overflow-hidden", className)}
      style={{
        backgroundColor: 'var(--bg-surface)',
        borderColor: 'var(--border-default)'
      }}
      data-testid={testId}
    >
      {/* Header */}
      <div
        className="px-4 py-3 border-b"
        style={{
          backgroundColor: 'var(--bg-elevated)',
          borderColor: 'var(--border-subtle)'
        }}
      >
        <h3
          className="text-sm font-mono uppercase tracking-wider flex items-center gap-2"
          style={{
            fontFamily: 'var(--font-family-mono)',
            color: 'var(--accent)'
          }}
        >
          <BarChart3 className="w-4 h-4" />
          QC Summary
        </h3>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Persona and Score */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" style={{ color: 'var(--accent)' }} />
            <div>
              <div
                className="text-sm font-medium"
                style={{
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--fg-primary)'
                }}
              >
                {qcSummary.personaSlug || 'Unknown'}
              </div>
              <div
                className="text-xs"
                style={{ color: 'var(--fg-muted)' }}
              >
                Persona
              </div>
            </div>
          </div>

          <div className="text-right">
            <div
              className="text-lg font-mono font-bold"
              style={{
                fontFamily: 'var(--font-family-mono)',
                color: decisionDisplay.color
              }}
            >
              {formatScore(qcSummary.weightedScore || 0)}
            </div>
            <div
              className="text-xs"
              style={{ color: 'var(--fg-muted)' }}
            >
              Weighted Score
            </div>
          </div>
        </div>

        {/* Decision Badge */}
        <div
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full border"
          style={{
            backgroundColor: `${decisionDisplay.color}20`,
            borderColor: decisionDisplay.color,
            color: decisionDisplay.color
          }}
        >
          {decisionDisplay.icon}
          <span
            className="text-sm font-medium"
            style={{ fontFamily: 'var(--font-family-mono)' }}
          >
            {decisionDisplay.label}
          </span>
        </div>

        {/* Sub-scores */}
        {qcSummary.subScores && Object.keys(qcSummary.subScores).length > 0 && (
          <div>
            <div
              className="text-xs font-mono uppercase tracking-wider mb-2"
              style={{
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--fg-secondary)'
              }}
            >
              Category Scores
            </div>
            <div className="space-y-2">
              {Object.entries(qcSummary.subScores).map(([category, score]) => (
                <div key={category} className="flex items-center justify-between">
                  <span
                    className="text-sm capitalize truncate"
                    style={{ color: 'var(--fg-primary)' }}
                  >
                    {category.replace(/_/g, ' ')}
                  </span>
                  <div className="flex items-center gap-2">
                    <div
                      className="w-16 h-2 rounded-full overflow-hidden"
                      style={{ backgroundColor: 'var(--bg-elevated)' }}
                      role="progressbar"
                      aria-valuenow={Math.round(score * 100)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label={`${category.replace(/_/g, ' ')} score`}
                    >
                      <div
                        className="h-full transition-all"
                        style={{
                          width: `${Math.min(100, Math.max(0, score * 100))}%`,
                          backgroundColor: score >= 0.7 ? 'var(--success)' : score >= 0.4 ? 'var(--warning)' : 'var(--danger)'
                        }}
                      />
                    </div>
                    <span
                      className="text-xs font-mono w-8 text-right"
                      style={{
                        fontFamily: 'var(--font-family-mono)',
                        color: 'var(--fg-secondary)'
                      }}
                      aria-label={`Score: ${formatScore(score)}`}
                    >
                      {formatScore(score)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metadata */}
        <div
          className="pt-3 border-t text-xs space-y-1"
          style={{
            borderColor: 'var(--border-subtle)',
            color: 'var(--fg-muted)'
          }}
        >
          {qcSummary.updatedAt && (
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              Updated: {formatTimestamp(qcSummary.updatedAt)}
            </div>
          )}
          {qcSummary.rubricHash && (
            <div
              className="font-mono"
              style={{ fontFamily: 'var(--font-family-mono)' }}
            >
              Rubric: {qcSummary.rubricHash.slice(0, 8)}...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}